import type { Rule } from 'antd/es/form'
import type { ConfigField } from '../utils/configTypes'

export function useConfigValidation(field: ConfigField) {
  const precision = field.unit === '%' ? 2 : field.unit === 'hrs' ? 0 : 2

  const getValidationRules = (configType: string): Rule[] => [
    ({ getFieldValue }) => ({
      validator: (_, value) => {
        // Only validate if the switch is enabled
        const enabled = getFieldValue([configType, field.key, 'enabled'])
        if (!enabled) return Promise.resolve()

        // Validate required and minimum value
        if (value === undefined || value === null) {
          return Promise.reject('Required')
        }
        if (typeof value === 'number') {
          const minValue = field.unit === '%' ? 0.0001 : 0.01 // 0.01% vs 0.01
          if (value < minValue) {
            const displayMin = field.unit === '%' ? '0.01%' : '0.01'
            return Promise.reject(`Must be greater than ${displayMin}`)
          }
        }
        // Validate maximum for percentage fields
        if (field.unit === '%' && typeof value === 'number' && value > 1) {
          return Promise.reject('Must be 100% or less')
        }
        return Promise.resolve()
      },
    }),
  ]

  const normalize = (value: number) => {
    if (field.unit === '%') {
      // Convert percentage to decimal with simple rounding
      return Math.round(value * 100) / 10000
    }
    return value
  }

  const getValueProps = (value: number | undefined) => {
    if (field.unit === '%') {
      // Convert decimal back to percentage for display with simple rounding
      return { value: value ? Math.round(value * 10000) / 100 : undefined }
    }
    return { value }
  }

  const parser = (value: string | undefined) => {
    if (!value) return ''
    // Remove non-numeric characters except decimal point
    const cleanValue = value.replace(/[^0-9.]/g, '')
    // Ensure only one decimal point
    const parts = cleanValue.split('.')
    if (parts.length > 2) {
      return parts[0] + '.' + parts.slice(1).join('')
    }
    return cleanValue
  }

  const formatter = (value: string | number | undefined) => {
    if (!value && value !== 0) return ''
    // Don't force decimal places if user entered a whole number
    const num = typeof value === 'string' ? parseFloat(value) : value
    if (isNaN(num)) return ''

    // Check if the original value was a whole number using simple comparison
    if (field.unit === '%' && num % 1 === 0) {
      return Math.round(num).toString()
    }

    // For non-whole numbers, show the appropriate precision
    return num.toString()
  }

  return {
    precision,
    getValidationRules,
    normalize,
    getValueProps,
    parser,
    formatter,
  }
}
